import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["active", "aria-checked", "block", "children", "disabled", "download", "external", "hideExternalLinkIcon", "href", "iconProps", "id", "onBlur", "onClick", "onFocus", "onKeyDown", "onKeyUp", "onMouseDown", "ping", "referrerPolicy", "responsive", "size", "tabIndex", "target", "truncate", "truncateStringProps", "truncateTooltip", "use", "rel"];
import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { getSizeTokens, getUseTokens, isLinkUse, isFormUse, isLinkUseAlwaysUnderlined, setAsActive } from './Button.helpers';
import { renderChildren, renderExternalButtonIcon, renderTruncatedContents } from './Button.utils';
import styled, { useTheme } from 'foundations-theming/utils';
import { useResponsiveStyles, useSizeCssObject, useUseVariantCssObject } from './Button.hooks';
import { customizeStylesWithImportant } from '../../../utils/customizeStylesWithImportant';
import emptyFunction from 'react-utils/emptyFunction';
import { filterProps } from '../../../utils/filterProps';
import { getIsSafeUrl } from '../../../components/Link/Link.helpers';
import { mergeProps } from 'react-aria';
import { useIconButtonContext } from '../IconButton/IconButtonProvider';
import { useLoadingButtonContext } from '../LoadingButton/LoadingButtonProvider';
import { useMapTokenPropsToCSS } from '../../../hooks/useMapTokenPropsToCSS';
import { deepMerge } from '../../utils/deepMerge/deepMerge';
import { useAbstractDropdownContext } from '../AbstractDropdown/AbstractDropdownProvider';
import { jsxs as _jsxs } from "react/jsx-runtime";
export const UnstyledButtonLink = styled.a.withConfig({
  displayName: "PrivateButtonLink__UnstyledButtonLink",
  componentId: "x61s0h-0"
})(({
  $block,
  $responsiveStyles,
  $spaceCssStyles,
  $isLinkUse,
  $styleOverrides
}) => {
  return deepMerge(Object.assign({
    all: 'unset',
    background: 'none',
    border: 0,
    padding: 0,
    cursor: 'pointer',
    textAlign: 'center',
    textDecoration: 'none'
  }, $spaceCssStyles, $responsiveStyles, $block && {
    width: '100%'
  }, {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center'
  }, $isLinkUse && {
    fontWeight: 600,
    fontSize: 'inherit',
    lineHeight: 'inherit'
  }, {
    '&:hover': {
      textDecoration: 'underline'
    }
  }), $styleOverrides);
});
export const StyledButtonLink = styled.a.withConfig({
  displayName: "PrivateButtonLink__StyledButtonLink",
  componentId: "x61s0h-1"
})(({
  $alwaysUnderline,
  $block,
  $responsiveStyles,
  $sizeCssStyles,
  $spaceCssStyles,
  $typography,
  $variantCssStyles,
  $isLinkUse,
  theme,
  $styleOverrides,
  $use,
  $size
}) => {
  return deepMerge(Object.assign({}, $variantCssStyles, $sizeCssStyles, $spaceCssStyles, $responsiveStyles, theme.typography[$typography], $size === 'small' && {
    fontWeight: theme.typography['body-700'].fontWeight
  }, $block && {
    width: '100%'
  }, $isLinkUse && {
    fontWeight: 600,
    fontSize: 'inherit',
    lineHeight: 'inherit'
  }, {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: isLinkUse($use) || isFormUse($use) ? 'left' : 'center',
    textAlign: isLinkUse($use) || isFormUse($use) ? 'left' : 'center',
    textDecoration: $alwaysUnderline ? 'underline' : 'none',
    transition: `all 150ms ease-out`,
    borderRadius: theme.borderRadius['100'],
    // The border radius is always applied so that there aren't any border radius changes when the focus ring appears

    '& > * + *': {
      marginInlineStart: theme.space['200']
    }
  }), $styleOverrides);
});
export const PrivateButtonLink = /*#__PURE__*/forwardRef((_ref, ref) => {
  var _iconButtonContext$bu, _loadingButtonContext, _abstractDropdownCont;
  let {
      active = false,
      'aria-checked': ariaChecked,
      block,
      children,
      disabled,
      download,
      external,
      hideExternalLinkIcon = false,
      href: hrefProp,
      iconProps,
      id,
      onBlur = emptyFunction,
      onClick = emptyFunction,
      onFocus = emptyFunction,
      onKeyDown = emptyFunction,
      onKeyUp = emptyFunction,
      onMouseDown = emptyFunction,
      ping,
      referrerPolicy,
      responsive,
      size,
      tabIndex,
      target,
      truncate,
      truncateStringProps,
      truncateTooltip,
      use,
      rel = target === '_blank' ? 'noopener noreferrer' : undefined
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const linkElementRef = useRef(null);
  const useTokens = getUseTokens(use);
  if (active) {
    setAsActive(useTokens);
  }
  const linkProps = Object.assign({
    onBlur,
    onClick,
    onFocus,
    onKeyDown,
    onKeyUp,
    onMouseDown
  }, rest);
  const useVariantCssStyles = useUseVariantCssObject(isLinkUse(use), useTokens);
  const sizeTokens = getSizeTokens(size, use);
  const sizeCssStyles = useSizeCssObject(sizeTokens);
  const iconButtonContext = useIconButtonContext();
  const loadingButtonContext = useLoadingButtonContext();
  const abstractDropdownContext = useAbstractDropdownContext();
  const theme = useTheme();
  const {
    domProps,
    tokenProps,
    callbackProps
  } = filterProps(linkProps);
  const sharedStyleOverrides = deepMerge((_iconButtonContext$bu = iconButtonContext === null || iconButtonContext === void 0 ? void 0 : iconButtonContext.buttonStyleOverrides) !== null && _iconButtonContext$bu !== void 0 ? _iconButtonContext$bu : {}, (_loadingButtonContext = loadingButtonContext === null || loadingButtonContext === void 0 ? void 0 : loadingButtonContext.getButtonStyleOverrides(theme)) !== null && _loadingButtonContext !== void 0 ? _loadingButtonContext : {});
  const newSharedStyleOverrides = deepMerge(sharedStyleOverrides, (_abstractDropdownCont = abstractDropdownContext === null || abstractDropdownContext === void 0 ? void 0 : abstractDropdownContext.buttonStyleOverrides) !== null && _abstractDropdownCont !== void 0 ? _abstractDropdownCont : {});
  const customizeStyles = customizeStylesWithImportant(/^margin/);
  const spaceCssStyles = useMapTokenPropsToCSS(tokenProps, {
    customizeStyles
  });
  const responsiveStyles = useResponsiveStyles(responsive);
  const href = getIsSafeUrl(hrefProp) ? hrefProp : undefined;
  const isPlaceholder = href === undefined;

  /*
   * Adds the `ping` attribute to the `a` element when the `ping` prop changes.
   * This fixes the bug reported in https://git.hubteam.com/HubSpot/foundations-components/issues/677
   */
  useEffect(() => {
    if (ping !== undefined) {
      var _linkElementRef$curre;
      (_linkElementRef$curre = linkElementRef.current) === null || _linkElementRef$curre === void 0 || _linkElementRef$curre.setAttribute('ping', ping);
    }
  }, [ping]);
  useImperativeHandle(ref, () => linkElementRef.current, [linkElementRef]);
  const handleClick = e => {
    if (isPlaceholder) {
      e.stopPropagation(); // Prevents the click event from bubbling up when the link is a placeholder
      e.preventDefault(); // Prevents the link from navigating on click when the link is a placeholder
    } else if (typeof onClick === 'function') {
      onClick(e);
    }
  };
  const handleKeyDown = e => {
    if (isPlaceholder) {
      // We don't want to `preventDefault` because it would prevent keyboard and screen reader navigation

      e.stopPropagation(); // Prevents the keydown event from bubbling up when the link is a placeholder
    } else if (typeof onKeyDown === 'function') {
      onKeyDown(e);
    }
  };
  const mergedProps = mergeProps(domProps, callbackProps);
  const renderedChildren = renderChildren(children);
  const sizeToIconSize = {
    medium: '400',
    small: '300',
    'extra-small': '200'
  };
  const renderedExternalButtonIcon = !hideExternalLinkIcon ? renderExternalButtonIcon(external, Object.assign({
    color: use === 'link' ? 'icon-core-subtle' : undefined,
    size: sizeToIconSize[size]
  }, iconProps)) : null;
  const renderedContents = truncate ? renderTruncatedContents({
    truncateStringProps,
    truncateTooltip,
    renderedChildren,
    renderedExternalButtonIcon,
    use
  }) : renderedChildren;
  if (use === 'unstyled') {
    return /*#__PURE__*/_jsxs(UnstyledButtonLink, Object.assign({}, mergedProps, {
      onClick: handleClick,
      onKeyDown: handleKeyDown,
      id: id,
      "aria-disabled": disabled,
      "aria-checked": ariaChecked,
      href: href,
      target: target,
      rel: rel,
      referrerPolicy: referrerPolicy,
      download: download,
      ping: ping,
      tabIndex: tabIndex,
      ref: linkElementRef,
      $block: block,
      $spaceCssStyles: spaceCssStyles,
      $responsiveStyles: responsiveStyles,
      $isLinkUse: isLinkUse(use),
      $styleOverrides: newSharedStyleOverrides,
      children: [renderedContents, !truncate && external && renderedExternalButtonIcon]
    }));
  }
  return /*#__PURE__*/_jsxs(StyledButtonLink, Object.assign({}, mergedProps, {
    onClick: handleClick,
    onKeyDown: handleKeyDown,
    id: id,
    "aria-disabled": disabled,
    "aria-checked": ariaChecked,
    href: href,
    target: target,
    rel: rel,
    referrerPolicy: referrerPolicy,
    download: download,
    ping: ping,
    tabIndex: tabIndex,
    $alwaysUnderline: isLinkUseAlwaysUnderlined(use),
    $block: block,
    $variantCssStyles: useVariantCssStyles,
    $sizeCssStyles: sizeCssStyles,
    $spaceCssStyles: spaceCssStyles,
    $responsiveStyles: responsiveStyles,
    $typography: sizeTokens.typography,
    $isLinkUse: isLinkUse(use),
    ref: linkElementRef,
    $styleOverrides: newSharedStyleOverrides,
    $use: use,
    $size: size,
    children: [renderedContents, !truncate && external && renderedExternalButtonIcon]
  }));
});
PrivateButtonLink.displayName = 'PrivateButtonLink';